/** Longest category label a horizontal bar chart shows before it is shortened with an ellipsis. */
export const MAX_BAR_LABEL_LENGTH = 28;

const shorten = (label: string, maxLength: number): string => {
  if (label.length <= maxLength) {
    return label;
  }
  return `${label.slice(0, maxLength - 1).trimEnd()}…`;
};

/**
 * Category labels for a horizontal bar chart, one per bar, in the same order.
 *
 * Plotly draws one bar per distinct category, so two rooms with the same name (in different offices, or
 * twice in one) would be stacked into a single bar. A name that occurs more than once gets its qualifier
 * (the office) appended, and, if still not unique, a counter. Long labels are shortened so they fit
 * beside the chart; show the full name in the hover text.
 */
export const barChartLabels = (
  names: string[],
  qualifiers: string[],
  maxLength = MAX_BAR_LABEL_LENGTH,
): string[] => {
  const nameCounts = new Map<string, number>();
  names.forEach((name) => nameCounts.set(name, (nameCounts.get(name) ?? 0) + 1));

  const used = new Map<string, number>();
  return names.map((name, index) => {
    const qualified = (nameCounts.get(name) ?? 0) > 1 && qualifiers[index] ? `${name} · ${qualifiers[index]}` : name;
    let label = shorten(qualified, maxLength);
    const seen = used.get(label) ?? 0;
    used.set(label, seen + 1);
    if (seen > 0) {
      label = `${shorten(qualified, maxLength - 4)} (${seen + 1})`;
    }
    return label;
  });
};

/** Rough width of one character of the 12px chart font, for sizing the label column. */
const LABEL_CHAR_WIDTH = 7;
const LABEL_COLUMN_PADDING = 16;

export interface LeftAlignedLabels {
  /** Left margin wide enough for the longest label. */
  marginLeft: number;
  /** One annotation per label, drawn in the left margin and aligned to its left edge. */
  annotations: Array<Partial<Plotly.Annotations>>;
}

/**
 * Category labels for a horizontal bar chart, aligned to the left of the chart rather than right-aligned
 * against the bars as Plotly draws axis labels (it has no setting for that). Hide the axis's own tick labels
 * (`showticklabels: false`) and add these annotations; `labels` must be the bars' y values.
 */
export const leftAlignedLabels = (
  labels: string[],
  { minWidth = 90, maxWidth = 260 }: { minWidth?: number; maxWidth?: number } = {},
): LeftAlignedLabels => {
  const longest = labels.reduce((max, label) => Math.max(max, label.length), 0);
  const marginLeft = Math.min(maxWidth, Math.max(minWidth, longest * LABEL_CHAR_WIDTH + LABEL_COLUMN_PADDING));

  return {
    marginLeft,
    annotations: labels.map((label) => ({
      text: label,
      x: 0,
      xref: 'paper',
      // Pixels: from the plot's left edge back to the margin's left edge, plus a little breathing room.
      xshift: -marginLeft + 4,
      xanchor: 'left',
      align: 'left',
      y: label,
      yref: 'y',
      showarrow: false,
    })),
  };
};
